import { useDeferredValue, useEffect, useRef, useState, type FormEvent } from "react";
import { ArrowRight, Search } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import type { SearchEntityType, SearchResult } from "../lib/contracts";
import { endpoints } from "../lib/contracts";
import { request, withQuery } from "../lib/api";
import { formatRelative } from "../lib/format";
import { EmptyState, ErrorState, LoadingState } from "../components/ui/AsyncState";
import { PageHeader } from "../components/ui/PageHeader";
import { StatusBadge } from "../components/ui/Status";

const typeLabels: Record<SearchEntityType, string> = { task: "任务", note: "笔记", tool: "工具", purchase: "购买", learning_project: "学习项目", memory_card: "记忆卡片", memory: "记忆", daily_review: "每日复盘" };

export function SearchPage() {
  const [params, setParams] = useSearchParams();
  const [input, setInput] = useState(params.get("q") ?? "");
  const [type, setType] = useState(params.get("type") ?? "");
  const deferred = useDeferredValue(input);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);

  useEffect(() => {
    const query = deferred.trim();
    if (query.length < 2) { setResults([]); setLoading(false); return; }
    const timer = window.setTimeout(async () => {
      const id = ++requestId.current;
      setLoading(true); setError(null);
      try {
        const data = await request<SearchResult[]>(withQuery(endpoints.search, { q: query, type, limit: 50 }));
        if (id === requestId.current) { setResults(data); setParams({ q: query, ...(type ? { type } : {}) }, { replace: true }); }
      } catch (reason) { if (id === requestId.current) setError(reason instanceof Error ? reason.message : "搜索失败"); }
      finally { if (id === requestId.current) setLoading(false); }
    }, 300);
    return () => window.clearTimeout(timer);
  }, [deferred, type, setParams]);

  const submit = (event: FormEvent) => { event.preventDefault(); setInput((value) => value.trim()); };
  return (
    <div className="page-container">
      <PageHeader title="全局搜索" description="搜索任务、笔记、记忆、工具、购买记录、学习项目、记忆卡片和每日复盘。" />
      <form className="card flex flex-col gap-3 p-4 sm:flex-row" onSubmit={submit} role="search">
        <label className="relative flex-1"><span className="sr-only">搜索工作台</span><Search className="absolute left-3 top-3 text-muted" size={19} /><input className="input pl-10" autoFocus value={input} onChange={(event) => setInput(event.target.value)} placeholder="输入至少 2 个字符" /></label>
        <label><span className="sr-only">内容类型</span><select className="input min-w-40" value={type} onChange={(event) => setType(event.target.value)}><option value="">全部类型</option>{Object.entries(typeLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
        <button className="btn-primary" type="submit"><Search size={18} />搜索</button>
      </form>
      <div className="mt-5">
        {input.trim().length < 2 ? <EmptyState title="搜索以前记录过的内容" description="输入标题、正文或分类中的关键词，至少需要 2 个字符。" /> : loading ? <LoadingState label="正在搜索" /> : error ? <ErrorState message={error} /> : results.length === 0 ? <EmptyState title="没有找到匹配内容" description="尝试更换关键词、切换内容类型，或检查输入是否完整。" action={<button className="btn-secondary" onClick={() => { setInput(""); setType(""); }}>清除搜索</button>} /> : (
          <section className="card overflow-hidden"><p className="border-b px-4 py-3 text-sm text-muted" role="status">找到 {results.length} 条结果</p><ul className="divide-y">{results.map((result) => <li key={`${result.type}-${result.id}`}><Link to={result.path} className="flex min-h-24 items-center gap-4 px-4 py-3 hover:bg-subtle"><div className="min-w-0 flex-1"><div className="flex items-center gap-2"><StatusBadge tone="info">{typeLabels[result.type]}</StatusBadge><h2 className="truncate font-semibold">{result.title}</h2></div><p className="mt-2 line-clamp-2 text-sm text-muted">{result.excerpt}</p><p className="mt-1 text-xs text-[var(--color-text-subtle)]">更新于 {formatRelative(result.updatedAt)}</p></div><ArrowRight size={18} className="shrink-0 text-muted" aria-hidden="true" /></Link></li>)}</ul></section>
        )}
      </div>
    </div>
  );
}
